import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStatusSummary, lv } from './statusMeta';

/**
 * A88 — full-width strip across the app, shown only when something needs every seller's attention:
 *   - an admin notice is active, or
 *   - something on OUR side is at partial/major outage, or
 *   - a courier / sales channel THIS seller uses is at partial/major outage.
 * A courier outage for a courier the seller does not use never reaches them here.
 * Dismissible for the session; a different problem brings it back.
 */
const KEY = 'mc_status_banner_dismissed';

export default function StatusBanner() {
  const s = useStatusSummary();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem(KEY) || ''; } catch (_) { return ''; } });
  if (!s) return null;

  const notices = s.notices || [];
  const outages = (s.issues || []).filter(i => lv(i.status).rank >= 2 && (
    (i.scope === 'platform' && i.owner === 'modcommerce')
    || (i.scope === 'account' && (i.owner === 'courier' || i.owner === 'channel'))
  ));
  if (!notices.length && !outages.length) return null;

  const sig = [...notices.map(n => `n${n.id}:${n.level}`), ...outages.map(o => `${o.key}:${o.status}`)].join('|');
  if (dismissed === sig) return null;

  const worst = [...notices.map(n => (n.level === 'info' ? 'maintenance' : n.level)), ...outages.map(o => o.status)]
    .reduce((a, b) => (lv(b).rank > lv(a).rank ? b : a), 'maintenance');
  const L = lv(worst);
  const text = notices.length
    ? `${notices[0].title}${notices[0].message ? ` — ${notices[0].message}` : ''}`
    : `${outages[0].name}: ${outages[0].message}`;
  const more = notices.length + outages.length - 1;

  const dismiss = () => { try { sessionStorage.setItem(KEY, sig); } catch (_) { /* private mode */ } setDismissed(sig); };

  return (
    <div role="status" aria-live="polite" style={{ ...b.strip, background: L.bg, borderBottom: `1px solid ${L.border}`, color: '#0f172a' }}>
      <L.Ico size={16} style={{ color: L.color }} />
      <span style={b.msg}>{text}</span>
      {more > 0 && <span style={b.more}>+{more} more</span>}
      <button style={{ ...b.link, color: L.color }} onClick={() => navigate('/dashboard/status')}>View status</button>
      <button style={b.close} onClick={dismiss} aria-label="Dismiss">Dismiss</button>
    </div>
  );
}

const b = {
  strip: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 18px', fontSize: 13.5, lineHeight: 1.4 },
  msg: { fontWeight: 600, flex: '1 1 320px', minWidth: 0 },
  more: { fontSize: 12, color: '#64748b', fontWeight: 600 },
  link: { background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' },
  close: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12.5, padding: '0 2px' },
};
